import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Globe, 
  Users, 
  MessageCircle, 
  Brain,
  Sparkles,
  ArrowRight,
  BookOpen,
  Target,
  Heart,
  Zap,
  Languages,
  ChevronDown,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import DemoModal from '../components/DemoModal';
import { MinnowLogo, MinnowLogoFull } from '../components/MinnowLogo';
import i18n from '../i18n';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync with i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(lng);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
    // Store in localStorage for persistence and to use during signup
    localStorage.setItem('minnow_landing_language', langCode);
  };

  const currentLanguage = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  const handleSeeHowItWorks = () => {
    if (isMobile) {
      navigate('/demo');
    } else {
      setShowDemoModal(true);
    }
  };

  const handleDemoComplete = (action) => {
    if (action === 'signup') {
      navigate('/register');
    }
  };

  const problems = [
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: t('landing.problemJargon'),
      description: t('landing.problemJargonDesc')
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('landing.problemNetwork'),
      description: t('landing.problemNetworkDesc')
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: t('landing.problemGlobal'),
      description: t('landing.problemGlobalDesc')
    }
  ];

  const rooms = [
    {
      icon: <Globe className="w-10 h-10 text-sky-blue" />,
      title: t('landing.generalRoom'),
      description: t('landing.generalRoomDesc'),
      color: "bg-sky-blue/10 border-sky-blue/20"
    },
    {
      icon: <Users className="w-10 h-10 text-coral" />,
      title: t('landing.regionalRooms'),
      description: t('landing.regionalRoomsDesc'),
      color: "bg-coral/10 border-coral/20"
    },
    {
      icon: <Brain className="w-10 h-10 text-ocean-blue" />,
      title: t('landing.minnowRoom'),
      description: t('landing.minnowRoomDesc'),
      color: "bg-ocean-blue/10 border-ocean-blue/20"
    }
  ];

  const modules = [
    { title: t('landing.moduleEmotional'), icon: Heart, description: t('landing.moduleEmotionalDesc') },
    { title: t('landing.moduleCoreSkills'), icon: Target, description: t('landing.moduleCoreSkillsDesc') },
    { title: t('landing.moduleIdeaLab'), icon: Zap, description: t('landing.moduleIdeaLabDesc') },
    { title: t('landing.moduleGTM'), icon: Sparkles, description: t('landing.moduleGTMDesc') }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 bg-[#1a3a52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <MinnowLogoFull variant="white" logoSize={40} />
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-white/90 hover:text-white hover:bg-white/10 gap-1 sm:gap-2 px-2 sm:px-3"
                    data-testid="landing-language-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="hidden sm:inline">{currentLanguage.name}</span>
                    <span className="sm:hidden">{currentLanguage.flag}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className="flex items-center justify-between cursor-pointer"
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                      {currentLang === lang.code && (
                        <Check className="w-4 h-4 text-ocean-blue" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                variant="ghost" 
                className="text-white/90 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/login')}
                data-testid="login-nav-btn"
              >
                {t('common.login')}
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-white text-[#1a3a52] hover:bg-white/90 rounded-full px-4 sm:px-6"
                data-testid="register-nav-btn"
              >
                {t('common.getStarted')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-[#1a3a52] pt-32 pb-24 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
          </svg>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('landing.heroTitle')}<br />
                <span className="text-sky-blue">{t('landing.heroTitleHighlight')}</span>
              </h1>
              <p className="text-xl text-white/80 mb-8 max-w-lg">
                {t('landing.heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="bg-coral hover:bg-coral/90 text-white rounded-full px-8 text-lg h-14"
                  data-testid="hero-cta-btn"
                >
                  {t('landing.startLearningFree')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 text-lg h-14"
                  data-testid="hero-secondary-btn"
                  onClick={handleSeeHowItWorks}
                >
                  {t('landing.seeHowItWorks')}
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Floating cards illustration */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-mint rounded-2xl rotate-12 animate-float opacity-80" />
                <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-coral rounded-2xl -rotate-12 animate-float opacity-80" style={{ animationDelay: '1s' }} />
                <Card className="relative bg-white/10 backdrop-blur border-white/20 rounded-3xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-mint flex items-center justify-center">
                      <Brain className="w-7 h-7 text-ocean-blue" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{t('landing.minnowAI', 'Minnow AI')}</p>
                      <p className="text-white/60 text-sm">{t('landing.yourCompanion', 'Your learning companion')}</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-lg leading-relaxed">
                    "{t('landing.heroQuote', 'Welcome! Based on your background in design and goal to launch a SaaS, let\'s start with validating your idea...')}"
                  </p>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Wave divider */}
        <div className="wave-divider absolute bottom-0 left-0 right-0" />
      </section>

      {/* Problems Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t('landing.problemsTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.problemsSubtitle')}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-2xl border-2 border-border hover:border-ocean-blue/30 card-hover" data-testid={`problem-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-coral/10 flex items-center justify-center mx-auto mb-6 text-coral">
                      {problem.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{problem.title}</h3>
                    <p className="text-muted-foreground">{problem.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-sand-beige/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Wave illustration */}
              <svg viewBox="0 0 400 300" className="w-full h-auto">
                <defs>
                  <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1E4D7A" />
                    <stop offset="100%" stopColor="#4FA3D1" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,150 Q100,100 200,150 T400,150"
                  fill="none"
                  stroke="url(#waveGradient)"
                  strokeWidth="4"
                  className="animate-float"
                />
                <path
                  d="M0,180 Q100,130 200,180 T400,180"
                  fill="none"
                  stroke="#A8E6CF"
                  strokeWidth="3"
                  className="animate-float"
                  style={{ animationDelay: '0.5s' }}
                />
                <path
                  d="M0,210 Q100,160 200,210 T400,210"
                  fill="none"
                  stroke="#E8DCC2"
                  strokeWidth="2"
                  className="animate-float"
                  style={{ animationDelay: '1s' }}
                />
                <circle cx="80" cy="120" r="8" fill="#FF6F61" />
                <circle cx="200" cy="100" r="12" fill="#1E4D7A" />
                <circle cx="320" cy="130" r="6" fill="#A8E6CF" />
              </svg>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-ocean-blue/10 text-ocean-blue text-sm font-medium mb-6">
                {t('landing.solutionTag')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                {t('landing.solutionTitle')} <span className="text-ocean-blue">{t('landing.solutionTitleHighlight')}</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {t('landing.solutionSubtitle')}
              </p>
              <ul className="space-y-4">
                {[
                  t('landing.solutionPoint1'),
                  t('landing.solutionPoint2'),
                  t('landing.solutionPoint3'),
                  t('landing.solutionPoint4')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-mint flex items-center justify-center">
                      <svg className="w-4 h-4 text-ocean-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rooms Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t('landing.roomsTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.roomsSubtitle')}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {rooms.map((room, index) => (
              <motion.div
                key={room.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full rounded-2xl border-2 ${room.color} card-hover`} data-testid={`room-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">{room.icon}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{room.title}</h3>
                    <p className="text-muted-foreground">{room.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a3a52]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.modulesTitle')}
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              {t('landing.modulesSubtitle')}
            </p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((module, index) => (
              <motion.div
                key={module.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white/10 backdrop-blur border-white/20 rounded-2xl card-hover" data-testid={`module-card-${index}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                      <module.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{module.title}</h3>
                    <p className="text-white/60 text-sm">{module.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Wave divider */}
        <div className="wave-divider-reverse absolute bottom-0 left-0 right-0" style={{ transform: 'rotate(180deg)' }} />
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('landing.ctaSubtitle')}
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="gradient-ocean text-white rounded-full px-10 text-lg h-14"
              data-testid="cta-btn"
            >
              {t('landing.startLearningFree')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-[#1a3a52] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <MinnowLogoFull variant="white" logoSize={40} />
            <div className="flex items-center gap-8 text-sm text-white/70">
              <a href="#" className="hover:text-white transition-colors">{t('common.about')}</a>
              <a href="#" className="hover:text-white transition-colors">{t('common.terms')}</a>
              <a href="#" className="hover:text-white transition-colors">{t('common.privacy')}</a>
              <div className="flex items-center gap-2">
                <Languages className="w-4 h-4" />
                <span>{currentLanguage.name}</span>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-white/50">
            © 2024 Minnow School. {t('landing.footerTagline')}
          </div>
        </div>
      </footer>

      {/* Demo Modal - Desktop Only */}
      <DemoModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)}
        onComplete={handleDemoComplete}
        currentLang={currentLang}
      />
    </div>
  );
};

export default LandingPage;
